"use client";

import type { Transition } from "framer-motion";
import type { FeaturesContent } from "@/components/landing/registry/content";
import type { FontId, SectionId, TemplateVisual } from "@/components/landing/StoreCustomizer/types";
import { faCreditCard, faImage } from "@fortawesome/pro-solid-svg-icons";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { pickBi } from "@/components/landing/registry/locale";
import { FONT_VISUALS } from "@/components/landing/StoreCustomizer/config";
import { Icon } from "@/components/ui/Icon";

interface StorePreviewProps {
	livePreviewLabel: string;
	captionLabel: string;
	previewLang: "en" | "ar";
	onLangChange: (lang: "en" | "ar") => void;
	template: TemplateVisual;
	accent: string;
	fontId: FontId;
	logoUrl: string | null;
	visible: Record<SectionId, boolean>;
	transition: Transition;
	content: FeaturesContent;
}

function StorePreview(prop: StorePreviewProps) {
	const {
		livePreviewLabel,
		captionLabel,
		previewLang,
		onLangChange,
		template,
		accent,
		fontId,
		logoUrl,
		visible,
		transition,
		content,
	} = prop;
	const font = FONT_VISUALS[fontId];
	const [activeCategory, setActiveCategory] = useState(0);

	// Decorative demo storefront content — represents a merchant's own store,
	// not Jana's UI chrome. Falls back to the original hand-authored copy so an
	// unedited Studio section still looks exactly like today.
	const demo = {
		storeName: pickBi(
			content.demoStoreName,
			previewLang,
			previewLang === "ar" ? "حلويات روان" : "Rawan Sweets",
		),
		heroHeadline: pickBi(
			content.demoHeadline,
			previewLang,
			previewLang === "ar" ? "حلويات طازجة، كل يوم" : "Fresh desserts, made daily",
		),
		cta: pickBi(content.demoCta, previewLang, previewLang === "ar" ? "تسوق الآن" : "Shop now"),
		categories:
			content.demoCategories.length > 0
				? content.demoCategories.map((c) => pickBi(c, previewLang))
				: previewLang === "ar"
					? ["كيك", "بسكويت", "معجنات"]
					: ["Cakes", "Cookies", "Pastries"],
		productsTitle: pickBi(
			content.demoProductsTitle,
			previewLang,
			previewLang === "ar" ? "الكيك" : "Cakes",
		),
		products:
			content.demoProducts.length > 0
				? content.demoProducts.map((p) => ({
						name: pickBi(p.name, previewLang),
						price: pickBi(p.price, previewLang),
					}))
				: previewLang === "ar"
					? [
							{ name: "كيك الفستق", price: "4.500 د.ب" },
							{ name: "معمول التمر", price: "2.250 د.ب" },
						]
					: [
							{ name: "Pistachio cake", price: "4.500 BHD" },
							{ name: "Date maamoul", price: "2.250 BHD" },
						],
	};
	const dir = previewLang === "ar" ? "rtl" : "ltr";

	return (
		<div className="flex flex-col p-5">
			<div className="mb-3 flex items-center justify-between gap-3">
				<p className="text-xs font-semibold text-muted-foreground">{livePreviewLabel}</p>
				<div
					role="radiogroup"
					aria-label="Preview language"
					className="inline-flex items-center gap-0.5 rounded-full border border-border p-0.5"
				>
					{(["en", "ar"] as const).map((lang) => {
						const isActive = previewLang === lang;
						return (
							<button
								key={lang}
								type="button"
								role="radio"
								aria-checked={isActive}
								onClick={() => onLangChange(lang)}
								className="rounded-full px-2 py-0.5 text-[11px] font-bold uppercase transition-colors"
								style={{
									background: isActive ? accent : "transparent",
									color: isActive ? "#fff" : "var(--text-muted)",
								}}
							>
								{lang}
							</button>
						);
					})}
				</div>
			</div>

			<motion.div
				layout
				transition={transition}
				dir={dir}
				className="overflow-hidden border border-border pb-2"
				style={{ borderRadius: template.panelRadius, background: "#fefdfb", fontFamily: font.css }}
			>
				{font.href && <link rel="stylesheet" href={font.href} />}
				<div className="flex items-center justify-between gap-2 px-3.5 pt-3">
					<span
						className="flex items-center gap-1.5 text-[11px] font-bold"
						style={{ color: "#1c1c1c" }}
					>
						{logoUrl && (
							// oxlint-disable-next-line nextjs/no-img-element -- ephemeral blob: preview, not an optimizable asset
							<img src={logoUrl} alt="" className="h-4 w-4 rounded-full object-cover" />
						)}
						{demo.storeName}
					</span>
					<Icon icon={faCreditCard} size={14} className="text-[#8b93a1]" />
				</div>

				<AnimatePresence initial={false}>
					{visible.hero && (
						<motion.div
							key="hero"
							layout
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							transition={transition}
							className="overflow-hidden px-3.5 pt-3"
						>
							<div
								className="flex flex-col overflow-hidden"
								style={{
									borderRadius: template.heroRadius,
									background: accent,
									padding: template.padding,
									alignItems: template.align === "center" ? "center" : "flex-start",
									textAlign: template.align,
									transition: "background-color 0.4s ease",
								}}
							>
								<p className="max-w-[80%] text-sm font-bold leading-snug text-white">
									{demo.heroHeadline}
								</p>
								<span
									className="mt-3 px-3.5 py-1.5 text-[11px] font-bold"
									style={{
										borderRadius: template.buttonRadius,
										background: template.buttonStyle === "filled" ? "#fff" : "transparent",
										color: template.buttonStyle === "filled" ? accent : "#fff",
										border: template.buttonStyle === "outline" ? "1.5px solid #fff" : "none",
									}}
								>
									{demo.cta}
								</span>
							</div>
						</motion.div>
					)}
				</AnimatePresence>

				<AnimatePresence initial={false}>
					{visible.categories && (
						<motion.div
							key="categories"
							layout
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							transition={transition}
							className="overflow-hidden px-3.5"
						>
							<div
								role="radiogroup"
								aria-label={demo.productsTitle}
								className="flex flex-wrap gap-1.5 pt-3"
							>
								{demo.categories.map((cat, i) => {
									const isActive = i === activeCategory;
									return (
										<button
											key={cat}
											type="button"
											role="radio"
											aria-checked={isActive}
											onClick={() => setActiveCategory(i)}
											className="rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors"
											style={{
												background: isActive ? accent : "#eef1f4",
												color: isActive ? "#fff" : "#4b5563",
											}}
										>
											{cat}
										</button>
									);
								})}
							</div>
						</motion.div>
					)}
				</AnimatePresence>

				<AnimatePresence initial={false}>
					{visible.featured && (
						<motion.div
							key="featured"
							layout
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							transition={transition}
							className="overflow-hidden px-3.5 pb-3.5"
						>
							<p className="pt-3 text-xs font-bold" style={{ color: "#1c1c1c" }}>
								{activeCategory === 0 ? demo.productsTitle : demo.categories[activeCategory]}
							</p>
							<div className="mt-2 grid grid-cols-2 gap-2.5">
								{demo.products.map((product) => (
									<div key={product.name} className="flex flex-col gap-1.5">
										<div
											className="grid aspect-square place-items-center"
											style={{ background: "#eef1f4", borderRadius: template.heroRadius * 0.7 }}
										>
											<Icon
												icon={faImage}
												className="h-5 w-5 opacity-50"
												style={{ color: accent }}
											/>
										</div>
										<span className="text-[11px] font-medium" style={{ color: "#1c1c1c" }}>
											{product.name}
										</span>
										<span
											className="text-[11px] font-bold"
											style={{ color: accent, transition: "color 0.4s ease" }}
										>
											{product.price}
										</span>
									</div>
								))}
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</motion.div>

			<p className="mt-3 text-center text-[11px] text-muted-foreground">{captionLabel}</p>
		</div>
	);
}

export default StorePreview;
